import axios from 'axios';
import { getApiUrl } from '../../../config/api';

export default class MenuTableView1Controller {
  constructor() {
    this.menus = [];
    this.loading = true;
    this.error = null;
    this.menuToDelete = null;
    this.sortKey = 'Menu_id';
    this.sortDir = 'asc';
    this.filters = {
      titulo: '',
      enlace: '',
      estado: ''
    };
  }

  /**
   * Inicializa el controlador
   */
  async init() {
    await this.fetchMenus();
  }

  /**
   * Obtiene la lista de menús desde la API
   */
  async fetchMenus() {
    this.loading = true;
    this.error = null;
    
    try {
      const response = await axios.post(getApiUrl('/api/Menu/listar'));
      this.menus = response.data.OData || [];
    } catch (error) {
      console.error('Error al cargar menús:', error);
      this.error = 'No se pudieron cargar los menús. Por favor, intente nuevamente.';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Obtiene los menús filtrados y ordenados
   * @returns {Array} - Array de menús filtrados
   */
  get filteredMenus() {
    let result = [...this.menus];
    
    // Aplicar filtros
    if (this.filters.titulo) {
      result = result.filter(menu => 
        menu.Titulo && menu.Titulo.toLowerCase().includes(this.filters.titulo.toLowerCase())
      );
    }
    
    if (this.filters.enlace) {
      result = result.filter(menu => 
        menu.Enlace && menu.Enlace.toLowerCase().includes(this.filters.enlace.toLowerCase())
      );
    }
    
    if (this.filters.estado !== '') {
      const estado = this.filters.estado === 'true';
      result = result.filter(menu => menu.Es_activo === estado);
    }
    
    // Aplicar ordenamiento
    result.sort((a, b) => {
      let aValue = a[this.sortKey];
      let bValue = b[this.sortKey];
      
      // Manejar valores nulos
      if (aValue === null) aValue = '';
      if (bValue === null) bValue = '';
      
      // Ordenar según tipo de dato
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toString().toLowerCase();
      }
      
      if (this.sortDir === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    return result;
  }

  /**
   * Ordena por el campo especificado
   * @param {string} key - Campo por el cual ordenar
   */
  sortBy(key) {
    // Si hacemos clic en la misma columna, invertimos la dirección
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
  }

  /**
   * Obtiene el icono de ordenamiento para un campo
   * @param {string} key - Campo para el cual obtener el icono
   * @returns {string} - Clase CSS del icono
   */
  getSortIcon(key) {
    if (this.sortKey !== key) return 'bi bi-arrow-down-up text-muted';
    return this.sortDir === 'asc' ? 'bi bi-arrow-down' : 'bi bi-arrow-up';
  }

  /**
   * Actualiza un filtro específico
   * @param {string} filterName - Nombre del filtro
   * @param {string} value - Valor del filtro
   */
  updateFilter(filterName, value) {
    if (Object.prototype.hasOwnProperty.call(this.filters, filterName)) {
      this.filters[filterName] = value;
    }
  }

  /**
   * Resetea todos los filtros
   */
  resetFilters() {
    this.filters = {
      titulo: '',
      enlace: '',
      estado: ''
    };
  }

  /**
   * Establece el menú a eliminar
   * @param {Object} menu - Menú a eliminar
   */
  setMenuToDelete(menu) {
    this.menuToDelete = menu;
  }

  /**
   * Elimina el menú seleccionado
   * @returns {Promise<boolean>} - True si se eliminó correctamente
   */
  async deleteMenu() {
    if (!this.menuToDelete) return false;
    
    try {
      const response = await axios.post(getApiUrl('/api/Menu/borrar'), this.menuToDelete.Menu_id);
      
      // Actualizar lista
      await this.fetchMenus();
      
      return {
        success: true,
        message: response.data.message || 'Menú eliminado correctamente.'
      };
    } catch (error) {
      console.error('Error al eliminar menú:', error);
      return {
        success: false,
        message: 'No se pudo eliminar el menú. Por favor, intente nuevamente.'
      };
    } finally {
      this.menuToDelete = null;
    }
  }

  /**
   * Exporta los menús filtrados a Excel (CSV)
   */
  exportToExcel() {
    // Crear un array con los datos a exportar
    const data = this.filteredMenus.map(menu => ({
      ID: menu.Menu_id,
      Título: menu.Titulo,
      Descripción: menu.Descripcion,
      Enlace: menu.Enlace,
      'Menú Padre': menu.Menu_id_padre,
      Orden: menu.Orden,
      Estado: menu.Es_activo ? 'Activo' : 'Inactivo',
      'Menú Superior': menu.Es_top ? 'Sí' : 'No'
    }));
    
    if (data.length === 0) {
      return {
        success: false,
        message: 'No hay datos para exportar'
      };
    }
    
    // Crear un elemento de tabla temporal
    const table = document.createElement('table');
    
    // Crear encabezados
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    Object.keys(data[0] || {}).forEach(key => {
      const th = document.createElement('th');
      th.textContent = key;
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);
    
    // Crear filas de datos
    const tbody = document.createElement('tbody');
    data.forEach(item => {
      const row = document.createElement('tr');
      Object.values(item).forEach(value => {
        const td = document.createElement('td');
        td.textContent = value;
        row.appendChild(td);
      });
      tbody.appendChild(row);
    });
    table.appendChild(tbody);
    
    // Convertir tabla a CSV
    let csv = [];
    const rows = table.querySelectorAll('tr');
    
    for (const row of rows) {
      const rowData = [];
      const cols = row.querySelectorAll('td, th');
      
      for (const col of cols) {
        rowData.push('"' + (col.textContent || '').replace(/"/g, '""') + '"');
      }
      
      csv.push(rowData.join(','));
    }
    
    const csvContent = csv.join('\n');
    
    // Crear un blob y descargar
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'menus.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return {
      success: true,
      message: 'Archivo exportado correctamente'
    };
  }

  /**
   * Obtiene el estado de carga
   * @returns {boolean}
   */
  get isLoading() {
    return this.loading;
  }

  /**
   * Obtiene el error actual
   * @returns {string|null}
   */
  get currentError() {
    return this.error;
  }

  /**
   * Obtiene la lista completa de menús
   * @returns {Array}
   */
  get menusList() {
    return this.menus;
  }

  /**
   * Obtiene el menú seleccionado para eliminar
   * @returns {Object|null}
   */
  get selectedMenuToDelete() {
    return this.menuToDelete;
  }
}